import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, NavLink } from 'react-router-dom';
import { Dropdown, ProfilePicture } from '@getro/rombo';
import { Box, Flex, Text } from 'rebass/styled-components';

import { Check, CreditCard, ExternalLink, Plus, Settings, Users } from 'lucide-react';
import { addProtocolToUrl } from 'helpers/urlHelpers';
import { trackEvent } from 'helpers/analytics';
import { isCustomer as isCustomerFn } from '../../../services/tierGating';
import { toggleMenu } from '../../../features/mainMenu';
import { useResetCache } from '../../../hooks/useResetCache';
import { Card } from '../../atoms/Card';
import { networksSelector } from '../../../redux/selectors';
import ActiveNetwork from './activeNetwork';

export const NetworkOption = () => {
  const { networks, active } = useSelector(networksSelector);
  const dispatch = useDispatch();
  const resetCache = useResetCache();
  const { url: jobBoardUrl, logoUrl: logo, isManager, ...rest } = networks[active];
  const current = { logo, ...rest };

  const networkList = useMemo(
    () =>
      Object.values(networks).sort((n1, n2) => {
        const n1Label = n1.name || n1.domain;
        const n2Label = n2.name || n2.domain;
        return n1Label.localeCompare(n2Label);
      }),
    [networks],
  );

  const networkLinks = useMemo(() => {
    const links = [];

    if (isManager) {
      links.push({
        label: 'Network settings',
        to: `/networks/${rest.slug}/settings/general`,
        icon: Settings,
        target: '_self',
      });
    }

    if (!rest.isOnlyAlly) {
      links.push({
        label: 'Team members',
        to: `/networks/${rest.slug}/settings/team-members`,
        icon: Users,
        target: '_self',
      });
    }

    if (isCustomerFn(networks[active])) {
      links.push({
        label: 'Plans and billing',
        to: `/networks/${rest.slug}/settings/plans-and-billing`,
        icon: CreditCard,
        target: '_self',
      });
    }

    if (jobBoardUrl && rest?.features?.includes('job_board')) {
      links.push({
        label: 'Open Job Board',
        onClick: () => window.open(addProtocolToUrl(jobBoardUrl), '_blank'),
        icon: ExternalLink,
        target: '_blank',
        to: '',
      });
    }

    return links ?? [];
  }, [active, isManager, jobBoardUrl, networks, rest]);

  const onNetworkClick = () => {
    resetCache();
    const width = window.screen.availWidth < window.innerWidth ? window.screen.availWidth : window.innerWidth;
    if (width < 990) {
      dispatch(toggleMenu());
    }
  };

  return (
    <Box sx={{ pointerEvents: 'auto' }}>
      <Dropdown
        data-cy="network-selector-multiple"
        placement="bottom-start"
        triggerProps={{
          variant: 'styleless',
          children: <ActiveNetwork current={current} />,
        }}
        dropDownProps={{
          sx: {
            ml: '-8px',
            mt: '4px',
            maxHeight: 'calc(100% - 32px)',
            overflowY: 'auto',
            boxShadow: 'rgb(0 0 0 / 10%) 0px 1px 8px, rgb(0 0 0 / 15%) 0px 8px 24px',
            borderRadius: '4px',
            padding: 0,
          },
        }}
      >
        <Card p="0" width="280px">
          <Box px="8px" py="12px">
            {/* Current network header */}
            <Flex alignItems="center" sx={{ gap: '8px', mb: '8px' }}>
              <ProfilePicture imageUrl={logo} name={current.name} variant="square" width="24px" height="24px" />
              <Text
                fontWeight="500"
                fontSize="14px"
                color="text.dark"
                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {current.name}
              </Text>
            </Flex>

            {/* Network actions */}
            {networkLinks.length > 0 && (
              <Box
                sx={{
                  '& a': {
                    color: 'text.main',
                    textDecoration: 'none',
                    cursor: 'pointer',
                    fontSize: '14px',
                    lineHeight: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    py: '4px',
                    px: '4px',
                    ':hover': { color: 'text.dark' },
                  },
                }}
              >
                {networkLinks.map(({ label, icon, target = '_blank', ...props }) => (
                  <Link
                    key={label}
                    target={target}
                    rel="noopener noreferrer"
                    onClick={() => {
                      const width =
                        window.screen.availWidth < window.innerWidth ? window.screen.availWidth : window.innerWidth;
                      if (width < 990) dispatch(toggleMenu());
                    }}
                    {...props}
                  >
                    <Box as={icon} size="16px" flex="0 0 auto" />
                    <Box as="span">{label}</Box>
                  </Link>
                ))}
              </Box>
            )}
          </Box>

          <Box height="1px" bg="neutral.100" mx="8px" />

          {/* YOUR NETWORKS section */}
          {networkList.length > 1 && (
            <Box px="8px" py="8px">
              <Text
                as="div"
                sx={{
                  color: 'text.subtle',
                  fontSize: '12px',
                  lineHeight: '16px',
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  my: '8px',
                }}
              >
                Your Networks
              </Text>
              {networkList.map(({ id, slug, name, domain, logoUrl }) => {
                const isActive = rest.id === id;
                return (
                  <Flex
                    key={id}
                    as={NavLink}
                    to={`/networks/${slug}`}
                    onClick={onNetworkClick}
                    alignItems="center"
                    sx={{
                      color: 'text.main',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      fontSize: '14px',
                      lineHeight: '24px',
                      gap: '8px',
                      py: '4px',
                      ':hover': { color: 'text.dark' },
                    }}
                  >
                    <ProfilePicture
                      imageUrl={logoUrl}
                      name={name || domain}
                      variant="square"
                      width="24px"
                      height="24px"
                      minWidth="24px"
                      minHeight="24px"
                    />

                    <Box
                      as="span"
                      flex="1"
                      sx={{
                        fontWeight: isActive ? '500' : '400',
                        color: isActive ? 'text.dark' : 'text.main',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        transition: 'all 0.3s ease',
                        ':hover': { color: 'text.dark', fontWeight: '500' },
                      }}
                    >
                      {name || domain}
                    </Box>
                    {isActive && (
                      <Box as={Check} size="16px" aria-hidden="true" sx={{ color: 'purple.500', flexShrink: 0 }} />
                    )}
                  </Flex>
                );
              })}
              <Box height="1px" bg="neutral.100" mt="8px" />
            </Box>
          )}

          {/* Create network */}
          <Box px="8px" py="4px">
            <Flex
              as="a"
              href="https://www.getro.com/demo"
              target="_blank"
              rel="noopener noreferrer"
              alignItems="center"
              sx={{
                color: 'text.main',
                textDecoration: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                lineHeight: '24px',
                gap: '8px',
                py: '6px',
                px: '8px',
                mx: '-8px',
                borderRadius: '4px',
                ':hover': { color: 'text.dark' },
              }}
              onClick={() => trackEvent('create_network_click', { network_id: active, origin: 'network_selector' })}
            >
              <Flex
                alignItems="center"
                justifyContent="center"
                sx={{ borderRadius: '4px' }}
                width="24px"
                height="24px"
                bg="neutral.50"
              >
                <Box as={Plus} size="16px" />
              </Flex>
              <Box as="span">Create network</Box>
            </Flex>
          </Box>
        </Card>
      </Dropdown>
    </Box>
  );
};
